import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useStateRef from 'react-usestateref';

import { PlaidLinkOnSuccess, PlaidLinkOnEvent, PlaidLinkStableEvent, PlaidLinkOnEventMetadata } from 'react-plaid-link';
import { sendPlaidSuccess, getCheckingAccounts, getApplicationData } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'handlers';
import { PlaidEvent, usePlaidLinkSetup } from 'hooks/usePlaidLinkSetup';
import { failureNotification } from 'notifications';

import Button from 'components/Button';

import { setPublicToken } from 'handlers/plaidPublicToken';
import { ReactComponent as PlaidLogo } from 'images/plaid.svg';
import { ReactComponent as CheckIcon } from 'images/green-check-rounded.svg';
import { StepComponent } from 'components/Steps/stepUtils';
import FormContainer from 'components/LoanForm/FormContainer';
import Loader from 'components/Loader';
import PartnerBanner from 'components/Common/PartnerBanner';
import BankAccount from 'components/Verification/Steps/CheckingAccount/BankAccount';
import SelectAccount from 'components/Verification/Steps/CheckingAccount/SelectAccount';
import ConfirmAccount from 'components/Verification/Steps/CheckingAccount/ConfirmAccount';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';
import { VerificationStep, useVerificationSteps } from 'components/Verification/verificationSteps';
import useCurrentFlow from 'hooks/useCurrentFlow';
import FormNavigation from 'components/FormNavigation';
import { FormNavigationProps } from 'components/FormNavigation/FormNavigation';

import { CheckingAccountResult } from 'enums/FlowNextResults';
import { PlaidAccountError } from 'api/PlaidApi';
import usePlaidValidateIdentity from 'hooks/usePlaidValidateIdentity';
import { isPlaidTokenValid } from 'utils/isPlaidTokenValid';
import { getBankAccount } from 'selectors/getBankAccount';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';

import { descriptionItems, aiCoachDescriptionItems, incomeVerificationDescriptionItems } from './plaidDescriptionItems';

import styles from './Plaid.module.scss';

interface PlaidProps extends StepComponent {
  isAchUpdateFlow?: boolean;
  NavigationComponent?: React.ComponentType<FormNavigationProps>;
  navigationInfo?: FormNavigationProps;
  onBackClick?: () => void;
}

const PlaidLink = ({
  handleNext,
  isAchUpdateFlow = false,
  NavigationComponent = FormNavigation,
  navigationInfo,
  onBackClick,
}: PlaidProps): JSX.Element => {
  const [exitedPlaid, setExitedPlaid] = useState<boolean>(false);
  const [plaidCompleted, setPlaidCompleted] = useState<boolean>(false);
  const [localPlaidPublicToken, setLocalPlaidPublicToken] = useState<string | null>(null);
  const [isSavingBankAccounts, setIsSavingBankAccounts] = useState<boolean>(false);
  const [isAutoRunning, setIsAutoRunning] = useState<boolean>(false);
  const autoRunExecuted = useRef(false);
  const { isLoadingCheckingAccounts } = useSelector(getBankAccount);

  const [incomeCompleted, setIncomeCompleted, incomeCompletedRef] = useStateRef<boolean>(false);

  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const { isMissedPaymentFlow, isAICoachFlow, isDebtConsolidationFlow } = useCurrentFlow();
  const { pathname } = useLocation();

  const { stepsProgress: verificationStepsStatus } = useVerificationSteps();
  const { application } = useSelector((state: RootState) => state.applicationData!);
  const { bankAccounts } = useSelector(getBankAccount);

  const applicationId = application?.id;
  const isAccountConnected = verificationStepsStatus[VerificationStep.CheckingAccount];
  const hasBankAccountConnected = bankAccounts.length > 0;
  const needsIncomeVerificationOnly = hasBankAccountConnected && isDebtConsolidationFlow;
  const isVerificationStep = application?.status === ApplicationStatusName.Verification;

  const {
    runValidateIdentity,
    clearPlaidAccountError,
    plaidAccountError,
    isLoadingSendPlaidSuccess,
  } = usePlaidValidateIdentity({
    applicationId,
  });

  // Runs automatically on entry in the verification flow when income is not yet verified
  // and the current flow is AI Coach.
  const shouldAutoValidateIdentity =
    !isAccountConnected && isAICoachFlow && isVerificationStep && hasBankAccountConnected;

  useEffect(() => {
    if (plaidCompleted && plaidAccountError) {
      setPlaidCompleted(false);
    }
  }, [plaidAccountError]);

  useEffect(() => {
    if (!applicationId || !shouldAutoValidateIdentity || autoRunExecuted.current) return;

    autoRunExecuted.current = true;

    const autoRun = async () => {
      setIsAutoRunning(true);
      try {
        dispatch(getCheckingAccounts({ applicationId }));
        const success = await runValidateIdentity();
        if (success) {
          setPlaidCompleted(true);
        }
      } finally {
        setIsAutoRunning(false);
      }
    };

    autoRun();
  }, [applicationId, shouldAutoValidateIdentity]);

  useEffect(() => {
    if (!plaidCompleted && application?.plaidTokenLastUpdated && isPlaidTokenValid(application.plaidTokenLastUpdated)) {
      if (isMissedPaymentFlow) {
        if (application.hasSignedACHForms) {
          return handleNext(CheckingAccountResult.MakePayment);
        }
        return handleNext(CheckingAccountResult.Continue);
      }
    }
  }, [application, plaidCompleted, isMissedPaymentFlow, isAccountConnected]);

  const handlePlaidSuccess = useCallback(
    async (plaidPublicToken: string, userConfirmedAccountOwnership = false) => {
      try {
        clearPlaidAccountError();
        setIsSavingBankAccounts(true);
        setLocalPlaidPublicToken(plaidPublicToken);
        // https://plaid.com/docs/api/tokens/#token-exchange-flow
        await dispatchWithUnwrap(
          sendPlaidSuccess({
            plaidPublicToken,
            applicationId: applicationId!,
            userConfirmedAccountOwnership,
            runIncomeVerification:
              !isAICoachFlow && !isAchUpdateFlow && (incomeCompleted || incomeCompletedRef.current),
          }),
        );
      } catch {
        setIsSavingBankAccounts(false);
        return;
      }
      const checkingAccounts = await dispatchWithUnwrap(getCheckingAccounts({ applicationId: applicationId! }));
      dispatch(setPublicToken({ publicToken: plaidPublicToken }));
      dispatchWithUnwrap(getApplicationData(applicationId!));
      setIsSavingBankAccounts(false);

      if (checkingAccounts.length === 0 && !isLoadingCheckingAccounts) {
        failureNotification('No checking accounts found. Please try connecting a different bank account.');
        setPlaidCompleted(false);
        return;
      }

      setPlaidCompleted(true);
      analytics.track('Checking Account Connected through Plaid');
    },
    [applicationId, dispatch, dispatchWithUnwrap, incomeCompleted],
  );

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (plaidPublicToken: string) => {
      handlePlaidSuccess(plaidPublicToken);
    },
    [handlePlaidSuccess],
  );

  const handleEvent = useCallback<PlaidLinkOnEvent>(
    (evtName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
      analytics.track(`Plaid Event: ${evtName}`, {
        metadata,
      });

      switch (evtName) {
        case PlaidEvent.BANK_INCOME_INSIGHTS_COMPLETED:
          setIncomeCompleted(true);
          break;
        case PlaidEvent.HANDOFF:
          setPlaidCompleted(true);
          break;
        default:
          break;
      }
    },
    [],
  );

  const { open, ready } = usePlaidLinkSetup({
    onSuccess,
    onEvent: handleEvent,
    onExit: () => setExitedPlaid(true),
    skipTokenCreation: isAccountConnected && !isMissedPaymentFlow && !isAchUpdateFlow,
    skipIncomeVerification: (isAICoachFlow && !isVerificationStep) || isAchUpdateFlow,
  });

  const handleConfirmOwnership = useCallback(async () => {
    if (localPlaidPublicToken) {
      await handlePlaidSuccess(localPlaidPublicToken, true);
    } else {
      const success = await runValidateIdentity({ userConfirmedAccountOwnership: true });
      if (success) {
        setPlaidCompleted(true);
      }
    }
  }, [localPlaidPublicToken, handlePlaidSuccess, runValidateIdentity]);

  const shouldUseMissedPaymentDescriptions = isMissedPaymentFlow || isAchUpdateFlow;
  const navigationProps =
    navigationInfo || (isMissedPaymentFlow ? ({ title: 'Missed Payment' } as FormNavigationProps) : undefined);
  if (isAutoRunning) {
    return (
      <FormContainer>
        <div className={styles.loaderContainer}>
          <Loader color="#795af7" />
        </div>
      </FormContainer>
    );
  }

  if (
    exitedPlaid &&
    (pathname === RoutePath.VerificationSteps || (pathname === RoutePath.Plaid && isMissedPaymentFlow))
  ) {
    return (
      <BankAccount
        handleNext={handleNext}
        isAchUpdateFlow={isAchUpdateFlow}
        NavigationComponent={NavigationComponent}
        navigationInfo={navigationInfo}
        onBackClick={onBackClick}
      />
    );
  }

  if (plaidAccountError === PlaidAccountError.ACCOUNT_OWNER_MISMATCH_ERROR) {
    return <ConfirmAccount onConfirm={handleConfirmOwnership} />;
  }

  if (plaidCompleted && !isLoadingSendPlaidSuccess) {
    return (
      <SelectAccount
        handleNext={handleNext}
        isAchUpdateFlow={isAchUpdateFlow}
        NavigationComponent={NavigationComponent}
        navigationInfo={navigationInfo}
        onBackClick={onBackClick}
      />
    );
  }

  const getSelectedDescriptionItems = () => {
    if (isAICoachFlow) return aiCoachDescriptionItems;
    if (needsIncomeVerificationOnly) return incomeVerificationDescriptionItems;
    return descriptionItems;
  };

  const getFormContainerProps = () => {
    if (needsIncomeVerificationOnly) {
      return {
        title: 'Verify your income',
        subtitle: 'We need to verify your income to complete your application.',
      };
    }
    return {
      title: 'Connect your checking account',
      subtitle: 'Connect the account where you deposit your paycheck and pay bills.',
    };
  };

  return (
    <>
      {navigationProps && <NavigationComponent {...navigationProps} />}
      <FormContainer {...getFormContainerProps()}>
        <PartnerBanner name="Plaid" logo={<PlaidLogo />} />

        <div className={styles.descriptionItems}>
          {getSelectedDescriptionItems().map((item, index) => {
            if (!isMissedPaymentFlow && item.description === null) return null;
            return (
              <div className={styles.item} key={index}>
                {item.missedPaymentIcon && shouldUseMissedPaymentDescriptions ? (
                  <item.missedPaymentIcon className={styles.icon} />
                ) : (
                  <item.icon className={styles.icon} />
                )}
                <div className={styles.itemText}>
                  {shouldUseMissedPaymentDescriptions ? item.missedPaymentDescription : item.description}
                </div>
              </div>
            );
          })}
        </div>

        {isAccountConnected && !isMissedPaymentFlow && !isAchUpdateFlow ? (
          <div className={styles.verified}>
            <CheckIcon className={styles.checkIcon} />
            <p className={styles.verifiedLabel}>Account connected</p>
          </div>
        ) : (
          <div className={styles.buttonContainer}>
            {plaidAccountError === PlaidAccountError.OTHER && (
              <div className={styles.plaidErrorContainer}>
                <UserSessionWarning
                  showWarningIcon={false}
                  showContactUsOption
                  message="Sorry, there was an unexpected error. Please try again"
                />
              </div>
            )}
            <Button onClick={() => open()} isLoading={!ready || !application || isSavingBankAccounts}>
              {isMissedPaymentFlow ? 'Link My Account' : 'Next'}
            </Button>
          </div>
        )}
      </FormContainer>
    </>
  );
};

export default PlaidLink;
